import { computed, onScopeDispose, ref, shallowRef, watch } from "vue";
import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";

import * as tmi from "@/constants";

import { useArticlesStore } from "@/stores/Articles";
import { type AuthResetOptions, useAuthStore } from "@/stores/Auth";
import { useAuthorsStore } from "@/stores/Authors";
import { useBonusReportsStore } from "@/stores/BonusReports";
import { useCategoriesStore } from "@/stores/Categories";
import { useCoursesStore } from "@/stores/Courses";
import { useModelPortfoliosStore } from "@/stores/ModelPortfolios";
import { useMyPortfoliosStore } from "@/stores/MyPortfolios";
import { usePermissionsStore } from "@/stores/Permissions";
import { useProductsStore } from "@/stores/Products";
import { useSectorsStore } from "@/stores/Sectors";
import { useSecuritiesStore } from "@/stores/Security";
import { useSimplePagesStore } from "@/stores/SimplePages";
import { useTickersStore } from "@/stores/Tickers";
import { useTradeAlertsStore } from "@/stores/TradeAlerts";
import { useUserStore } from "@/stores/User";
import { useWebinarsStore } from "@/stores/Webinars";

import { ContentfulItem, HasAuthor } from "@tmi/types/entities";

import { FiveMinutesInMilliseconds } from "@/constants";

import routeManager from "@/router/route-manager";
import type { Credentials } from "@tmi/types/authentication";
import {
    articleKey,
    authorsSyncKey,
    bonusReportKey,
    categoriesSyncKey,
    productsSyncKey,
    sectorAlertsKey,
    sectorsSyncKey,
    simplePagesSyncKey,
    tickersSyncKey,
    tradeAlertKey,
    webinarKey
} from "@/types/products/product-definitions";

import { RouteLocationNormalizedLoaded, useRouter } from "vue-router";

// UTILITIES
import { useSectorHistoryStore } from "@/stores/SectorHistory";
import { createDataSyncEvent, type DataSyncEvent } from "@/utils/data-sync-event-factory";
import {
    buildStandardHeaders,
    defaultReasonForAbortingApiCall,
    goFetch,
    isSuccessState,
    normalizeFetchParameters
} from "@/utils/network-utils";
import { reportContentViewEvent } from "@/composables/user-activity-monitor";
import { useContentReadState } from "@/composables/content-read-state";
import { recordTradeAlertArrivals, tradeAlertIdSnapshot } from "@/composables/new-trade-alerts";

import { MessageTypes } from "@tmi/types/enums/message-types";

import type { CompositionContext } from "@/services/data/sync-supervisor";
import type { RestfulPayload, SignUpPayload, LoginPayload } from "@tmi/types/api/fetch";
import { useLocalStorage } from "@vueuse/core";
import { ResumeCatchUpAfterMilliseconds, useAppResume } from "@/composables/useAppResume";
import { CacheClosedError, setUserScopedCacheGate } from "@/services/cache";
import { ensureCacheOwner, forgetCacheOwnerCheck } from "@/services/cache/cache-owner";
import { setStaleWindowReload } from "@/services/cache/cache-database";
import { resetCachedContent } from "@/services/cache/cache-maintenance";
import { clientStateManager, endClientErrorStreak, queueClientErrorOncePerStreak } from "@/services/error-services";
import {
    SESSION_ENDED_ELSEWHERE_EVENT,
    SESSION_EXPIRED_EVENT,
    type SessionEndedElsewhereDetail
} from "@/services/session-check";
import { Unauthorized } from "@tmi/types/api/responses";
import { deriveCacheOwnerId } from "@/utils/jwt-identity";
import { SignInReasonQueryKey, type SignInReason } from "@/utils/sign-in-notice";
import {
    lastSyncedAt,
    nextSyncKeyHealth,
    SYNC_BROADCAST_CHANNEL,
    type SyncHealth,
    type SyncStatusReport
} from "@/utils/sync-health";

import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

let worker: Worker | null = null;
/** A sync pass is running. A ref so a list's refresh control can show it (ListRefreshButton, UX roadmap #107). */
const syncInProgress = ref< boolean >( false );
/** Set once a newer build's cache upgrade has cost us our connection: this context is on its way out (R44). */
let reloadPending: boolean = false;
/** The one pending timer for the next pass (lifecycle-11): every re-arm goes through scheduleNextSync(). */
let nextSyncTimer: ReturnType< typeof setTimeout > | null = null;
/** When the last pass completed cleanly (epoch ms). 0 until this session's first one. */
let lastSyncAt: number = 0;
/** Passes in a row that did not complete cleanly; drives the retry backoff. */
let failedPasses: number = 0;
/** Bumped whenever the session is reset: a pass started for an earlier session is not this session's sync. */
let syncGeneration: number = 0;
let passGeneration: number = 0;
/** UX roadmap #58: whether the pass in flight started while the browser was offline, i.e. is bound to fail. */
let passStartedOffline: boolean = false;
/** UX roadmap #58: the device came back online during such a pass; the next one starts as soon as it ends. */
let resyncAfterPass: boolean = false;
/** UX roadmap #107: the pass in flight was a member's forced refresh (refreshContentNow), which skips the cooldowns. */
let passForced: boolean = false;
/** UX roadmap #107: a forced refresh was asked for while an ordinary pass ran; it starts as soon as that one ends. */
let forceAfterPass: boolean = false;
/** When the last forced pass started (epoch ms): repeated taps inside ForcedSyncFloor do not re-fetch everything. */
let lastForcedPassAt: number = 0;
/** UX roadmap #18: sync news shared with this member's other tabs. Opened with the worker, closed with it. */
let syncChannel: BroadcastChannel | null = null;

/** What a tab relays to the others: its worker's UPDATE_COMPLETE or SYNC_STATUS, and whose rows they describe. */
type SyncBroadcast = Pick< DataSyncEvent, 'eventType' | 'payload' | 'ok' | 'at' > & { owner: string | null };

const MaintenanceRefreshInterval: number = FiveMinutesInMilliseconds;
/** A failed pass retries at the content coordinators' own 15 s retry cooldown, doubling up to the full interval. */
const SyncRetryInterval: number = 15_000;
/** UX roadmap #107: a forced refresh re-reads every content catalog, so taps closer together than this fall back to resyncNow(). */
const ForcedSyncFloor: number = 15_000;

export const useApplicationStore = defineStore( 'application', () => {
    // Global references
    const router = useRouter();

    // Store references - used to clear state upon logout
    const articlesStore = useArticlesStore();
    const authStore = useAuthStore();
    const authorsStore = useAuthorsStore();
    const bonusReportsStore = useBonusReportsStore();
    const categoriesStore = useCategoriesStore();
    const coursesStore = useCoursesStore();
    const modelPortfoliosStore = useModelPortfoliosStore();
    const myPortfoliosStore = useMyPortfoliosStore();
    const permissionsStore = usePermissionsStore();
    const productsStore = useProductsStore();
    const sectorsStore = useSectorsStore();
    const sectorHistoryStore = useSectorHistoryStore();
    const securitiesStore = useSecuritiesStore();
    const simplePagesStore = useSimplePagesStore();
    const tickersStore = useTickersStore();
    const tradeAlertsStore = useTradeAlertsStore();
    const webinarsStore = useWebinarsStore();
    const userStore = useUserStore();
    const { markContentRead } = useContentReadState();

    /// STATE
    const { authToken, isAuthenticated } = storeToRefs( authStore );
    const adminSession = ref< boolean >( false );
    const authenticating = ref< boolean >( false );
    const handlingUnauthorized = ref< boolean >( false );
    // C5 fix round 1 (Finding 2): guards reHydrateTickerDependentContent against overlapping
    // passes — the tickers store and the crosswalk store can each independently flip from empty
    // to populated in the same burst (e.g. a cold load), and each fires its own UPDATE_COMPLETE.
    const reHydratingTickerDependentContent = ref< boolean >( false );

    const initializing = ref< boolean >( false );
    const publicInitialized = ref< boolean >( false );
    const privateInitialized = ref< boolean >( false );
    const shellBootstrapped = ref< boolean >( false );
    const bootstrappingShell = ref< boolean >( false );
    const loading = ref< boolean >( false );
    /** A count, not a flag: overlapping navigating sign-outs (the modal and the admin bar) each hold it until they land. */
    const signingOut = ref< number >( 0 );
    const isAuthenticating = computed< boolean >( () => authenticating.value );
    const isLoading = computed< boolean >( () => loading.value );
    /**
     * UX roadmap #24: a sign-out is on its way home. App.vue holds the routed page on its skeleton meanwhile: the layout
     * turns public as soon as the token is gone, and the private page used to remount inside it until the redirect.
     */
    const isSigningOut = computed< boolean >( () => signingOut.value > 0 );
    /** The session teardown in flight (endSessionAsync). A sign-in waits for it, and concurrent sign-outs share it. */
    let sessionTeardown: Promise< void > | null = null;
    const isShellReady = computed< boolean >( () => shellBootstrapped.value && !bootstrappingShell.value );
    /**
     * UX roadmap #9: whether the member shell's navigation can render without items popping in or flipping to locked.
     * It needs the product catalog (phase one of the boot) and the member's grants, never feature data. A member with no
     * grants never gets an initialized permission set, so a finished boot counts too.
     */
    const isNavigationReady = computed< boolean >( () =>
        isShellReady.value || ( publicInitialized.value && permissionsStore.isInitialized ) );

    /**
     * tmi-cya #6: both cache-eviction reloads — this window's own versionchange and the worker's RELOAD_REQUIRED —
     * wait out an in-flight sign-in, as the PWA controllerchange reload does (F-I3): a reload in the middle of a
     * token handoff can leave the user authenticated-but-freemium. The cache connection is already closed, so the
     * stores quiet-stop in the meantime. `reloadPending` keeps it to one reload and stops the sync loop re-arming.
     */
    function reloadOnceNotAuthenticating(): void {
        if ( reloadPending ) {
            return;
        }
        reloadPending = true;
        if ( !authenticating.value ) {
            window.location.reload();
            return;
        }
        logger.warn( '[ application ] a cache schema change needs a reload — waiting for the sign-in to settle' );
        const stop = watch( authenticating, busy => {
            if ( busy ) return;
            stop();
            window.location.reload();
        } );
    }
    setStaleWindowReload( reloadOnceNotAuthenticating );

    // UX roadmap #63: no user-scoped cache store is read before the cache is known to be this member's (cache-owner.ts).
    setUserScopedCacheGate( () => ensureCacheOwner( currentCacheOwner() ) );
    onScopeDispose( () => setUserScopedCacheGate( null ) );

    /*
     * data-11 / data-sync-1: while the tab is hidden the loop slows to one pass per MaintenanceRefreshInterval and only
     * its own timer starts passes; it catches up on return or reconnect. It used to stop outright, and then nothing
     * brought new trade alerts in while every TMI tab was in the background: the tab-title count, the app badge and the
     * toast held for the member's return (NewAlertAnnouncer) never fired, and on return the alert was too old to toast.
     */
    const visibility = useAppResume( resumeSync );
    watch( visibility, state => {
        // A failure's backoff retries (from 15 s) would poll in the background: they move to the hidden cadence. A clean
        // pass's timer is on that cadence already.
        if ( 'hidden' === state && null !== nextSyncTimer && failedPasses > 0 ) {
            scheduleNextSync( MaintenanceRefreshInterval );
        }
    } );

    /**
     * UX roadmap #58: each content key's last SYNC_STATUS (see utils/sync-health.ts). Offline or on a failing sync the
     * app used to show cached content as if it were current; ConnectionBanner reads this to say how old it is.
     */
    const syncHealth = shallowRef< SyncHealth >( {} );
    const currentSyncHealth = computed< SyncHealth >( () => syncHealth.value );
    /** When everything the member sees last synced cleanly on this device (epoch ms), null until a key reports one. */
    const lastContentSyncAt = computed< number | null >( () => lastSyncedAt( syncHealth.value ) );
    /** A sync pass is fetching content now: a member's refresh (resyncNow) or the loop's own. */
    const isSyncing = computed< boolean >( () => syncInProgress.value );

    const contentModalVisible = ref< boolean >( false );
    const contentItem = ref< HasAuthor | ContentfulItem | undefined >();
    const contentKey = ref< string | undefined >();

    const dateOfMostRecentMarketDataAvailable = useLocalStorage< string >( 'latest-dt', '' );

    const compositionCtx: CompositionContext = {
        getAuthorById: ( id: string ) => authorsStore.authorById( id ),
        getCategoryById: ( id: string ) => categoriesStore.categoryById( id ),
        getProductById: ( id: string ) => productsStore.productById( id ),
        getSectorById: ( id: string ) => sectorsStore.sectorById( id ),
        getSectorByTmiId: ( id: number ) => sectorsStore.sectorByTmiId( id ),
        getTickerById: ( id: string ) => tickersStore.tickerById( id ),
        // C5 (Option 1): content id -> symbol (crosswalk) -> sentient SearchableTicker (symbol).
        getTickerBySymbol: ( symbol: string ) => tickersStore.tickerBySymbol( symbol )
    };

    const supportedModelPortfoliosActions: Array< string > = [
        'switchPortfolioContext'
    ];
    modelPortfoliosStore.$onAction( async ( { name } ): Promise< void > => {
        // Rules:
        //  - must be logged in
        //  - the action being intercepted must be in the list of supported actions
        // The store itself skips a fresh copy and joins a refresh already in flight.
        if ( !isAuthenticated.value || !supportedModelPortfoliosActions.includes( name ) ) {
            return;
        }

        await modelPortfoliosStore.initializeAsync();
    } );

    /**
     * This call is not authenticated and thus does not need to first validate that the session is valid
     */
    async function refreshDateOfMostRecentMarketDataAvailable() {
        try {
            // tmi-ck2 C4: `dateOfLatestMarketDataAvailable`, the app's very FIRST request and the
            // only sentient call site that does not go through goFetch — it builds the request by
            // hand so the bootstrap cannot recurse into the session-check machinery. The twin is
            // one of the four anonymous sentient routes, so it still sends no token; it takes no
            // arguments, so it is a GET with no body.
            const fetchParams = normalizeFetchParameters( {
                serverBaseUri: `${ import.meta.env.VITE_SENTIENT_BASE_URI }`,
                endpoint: 'sentient/date-of-latest-market-data-available',
                verbOverride: 'GET'
            } );

            const headers = buildStandardHeaders();

            // Create an AbortController to short-circuit an API call that is taking too long
            const monitor = new AbortController();
            const timeoutId = setTimeout( () => monitor.abort( defaultReasonForAbortingApiCall ), fetchParams.maxApiDuration );

            const init = {
                method: fetchParams.method,
                body: fetchParams.body,
                headers: headers,
                signal: monitor.signal
            };

            // Perform the API call using fetch or any other suitable method
            const response: Response = await fetch( fetchParams.uri, init );


            // As a generally good practice, always clean up any resources that may or may not be active
            clearTimeout( timeoutId );

            if ( response.ok ) {
                let responsePayload: any = {};
                try {
                    responsePayload = await response.json();
                } catch ( err: any ) { /* IGNORE */
                }

                // NOTE the dropped `.data`. This call reads `response.json()` DIRECTLY rather
                // than going through goFetch, so there is no `ApiFetchResponse` wrapper here:
                // the REST body IS the GraphQL operation's `data` object
                // (`{ dateOfLatestMarketDataAvailable: { latest_mk_dt } }`), where the GraphQL
                // envelope nested it one level deeper under `data`.
                if ( responsePayload?.dateOfLatestMarketDataAvailable?.latest_mk_dt ) {
                    dateOfMostRecentMarketDataAvailable.value = responsePayload.dateOfLatestMarketDataAvailable.latest_mk_dt;
                }
            }
        } catch ( err: any ) {
            /* IGNORE */
        }
    }

    let marketDateRefresh: Promise< void > | null = null;

    /**
     * UX roadmap #9: refreshes 'latest-dt' but waits for it only while there is no date at all. With a cached date the
     * refresh runs behind the boot: every reader holds the storage ref and updates when the answer lands, and sector
     * history prefers its newest synced day anyway (DP-11). An empty 'latest-dt' still blocks the caller, because the
     * sector history load starts on it; the boot itself never waits (UX roadmap #19), only the private phase before
     * sector history does. Concurrent callers share one request. Never rejects.
     */
    function refreshMarketDateUnlessCached(): Promise< void > {
        if ( !marketDateRefresh ) {
            marketDateRefresh = refreshDateOfMostRecentMarketDataAvailable().finally( () => {
                marketDateRefresh = null;
            } );
        }

        return dateOfMostRecentMarketDataAvailable.value ? Promise.resolve() : marketDateRefresh;
    }

    /** The in-flight load of the composition catalogs, shared by every caller of compositionReadyAsync(). */
    let compositionLoad: Promise< void > | null = null;

    /**
     * UX roadmap #19: resolves once the catalogs hydrateContent() resolves against are loaded (products, authors,
     * categories, sectors, tickers; see compositionCtx). hydrateContent commits a non-reactive snapshot, so content
     * hydrated before these land keeps empty associations; nothing else it needs. A content deep link waits for this
     * alone, never for simple pages, the market date or the member's private stores. It is also the public phase of
     * runInitialization, so both share one load. Concurrent callers share it. Never rejects.
     */
    function compositionReadyAsync(): Promise< void > {
        if ( publicInitialized.value ) {
            return Promise.resolve();
        }

        if ( !compositionLoad ) {
            const load: Promise< void > = Promise.allSettled( [
                productsStore.initializeAsync(),
                authorsStore.initialize(),
                categoriesStore.initialize(),
                sectorsStore.initializeAsync(),
                tickersStore.initialize()
            ] ).then( () => {
                // A reset while this ran started a new session: its catalogs are not this load's to vouch for.
                if ( compositionLoad === load ) {
                    publicInitialized.value = true;
                    compositionLoad = null;
                }
            } );
            compositionLoad = load;
        }

        return compositionLoad;
    }

    /**
     * Shared handle on the run currently in flight, so concurrent callers JOIN it.
     *
     * This used to be `if ( initializing.value ) return;`, which handed every concurrent caller an
     * already-resolved promise while the real work was still running. There are 44 sites that
     * `await appStore.initializeAsync()` before reading store data; under that guard, all but the
     * first resolved in a single microtask and carried on against empty stores. It was invisible
     * only because nothing rendered until the shell's own call had finished — the moment public
     * routes stop waiting for the shell, every one of those 44 sites becomes a live race.
     */
    let initialization: Promise< void > | null = null;

    function initializeAsync(): Promise< void > {
        if ( initialization ) {
            return initialization;
        }

        initialization = runInitialization().finally( () => {
            initialization = null;
        } );

        return initialization;
    }

    async function runInitialization(): Promise< void > {
        try {
            initializing.value = true;
            if ( !publicInitialized.value ) {
                // UX roadmap #19: the market date is refreshed behind the boot (the private phase waits for it only
                // where sector history needs it), and simple pages load on demand from SimpleContentfulPage. Neither
                // is used by the composition context, yet an anonymous reader used to wait for both on every visit.
                void refreshMarketDateUnlessCached();
                await compositionReadyAsync();
            }

            // Required to validate other authorized stores
            if ( !privateInitialized.value && isAuthenticated.value ) {
                // Self-heal (LD2): profile + grants from the server when the cache is empty.
                // R45: only latch privateInitialized when the heal resolved true, so a later
                // navigation's successful heal still initializes the gated stores below.
                let permissionsReady = true;
                // UX roadmap #24: the member boot now runs behind the first page, so a sign-out can land while this
                // run waits below. Its latch must not carry over to the next member's session (see resetUserScopedState).
                const generation: number = syncGeneration;
                // UX roadmap #63: before any user-scoped read, the cache must be this member's. Checked beside the
                // heal: one is IndexedDB, the other the network.
                const cacheOwnerCheck: Promise< boolean > = cacheOwnerReadyAsync();
                if ( !permissionsStore.isInitialized ) {
                    permissionsReady = await permissionsStore.ensureInitializedAsync();
                }
                // Not this member's and it could not be cleared: the cached content stores sit this run out rather
                // than read the previous member's rows or latch on the failure. The next run checks again, and the
                // worker's own check (sync-supervisor.ts) retries the clear on its pass.
                const cacheReady: boolean = await cacheOwnerCheck;

                // UX roadmap #9: the awaited loads run side by side instead of one after another. Sector history stays
                // awaited: sites that `await initializeAsync()` read it straight after (see `initialization`).
                const awaitedLoads: Promise< void >[] = [];

                if ( cacheReady && permissionsStore.hasFeatureAccess( tmi.ArticlesPermissionKey ) ) {
                    articlesStore.initialize();
                }

                if ( cacheReady && permissionsStore.hasFeatureAccess( tmi.BonusReportsPermissionKey ) ) {
                    bonusReportsStore.initialize();
                }

                if ( cacheReady && permissionsStore.hasFeatureAccess( tmi.SectorAlertsPermissionKey ) ) {
                    // Sector history starts on 'latest-dt' while nothing is synced, so an empty one is waited for
                    // here (joining the refresh the public phase started); a cached one is not.
                    const marketDateReady: Promise< void > = dateOfMostRecentMarketDataAvailable.value
                        ? Promise.resolve()
                        : refreshMarketDateUnlessCached();
                    awaitedLoads.push( marketDateReady.then( () => sectorHistoryStore.initializeAsync() ) );
                }

                if ( cacheReady && permissionsStore.hasFeatureAccess( tmi.ProductTradeAlertsPermissionKey ) ) {
                    tradeAlertsStore.initialize();
                }

                // UX roadmap #9: not awaited. It is a network call (7 s timeout, one retry) that nothing outside the
                // model-portfolio screens reads, and those read the store reactively and load it themselves.
                if ( permissionsStore.hasModelPortfolioAccess ) {
                    void modelPortfoliosStore.initializeAsync();
                }

                if ( cacheReady && permissionsStore.hasFeatureAccess( tmi.WebinarsPermissionsKey ) ) {
                    webinarsStore.initialize();
                }

                if ( permissionsStore.hasFeatureAccess( tmi.PortfolioBuilderPermissionKey ) ) {
                    myPortfoliosStore.initialize().then();
                }

                // tmi-ck2.44: not awaited. Courses are a network call to the content service now (7 s timeout, and one
                // retry after a 503's Retry-After), and the course views render from the store's loadState themselves.
                if ( permissionsStore.hasFeatureAccess( tmi.CoursePermissionsKey ) ) {
                    void coursesStore.initializeAsync();
                }

                await Promise.allSettled( awaitedLoads );

                if ( generation === syncGeneration ) {
                    privateInitialized.value = permissionsReady && cacheReady;
                }
            }

            requestSyncAfterInitialization();
        } catch ( err: any ) {
            /* IGNORE */
        } finally {
            initializing.value = false;
        }
    }

    /**
     * Whether the cache may be read for the member signed in now (cache-owner.ts). A CacheClosedError stops quietly: a
     * reload is on its way. Any other failure is reported, since the member's lists stay empty for this run. Never rejects.
     */
    async function cacheOwnerReadyAsync(): Promise< boolean > {
        try {
            await ensureCacheOwner( currentCacheOwner() );
            return true;
        } catch ( err: unknown ) {
            if ( !( err instanceof CacheClosedError ) ) {
                logger.error( '[ application ] could not confirm the cached content belongs to this member', err );
                clientStateManager.queueClientError( err, { request: 'cache owner check' } );
            }
            return false;
        }
    }

    /** The boot in flight, so a sign-in can queue its own boot behind one started for the previous session. */
    let shellBoot: Promise< void > | null = null;

    function bootstrapShell(): Promise< void > {
        if ( shellBootstrapped.value || bootstrappingShell.value ) {
            return Promise.resolve();
        }

        bootstrappingShell.value = true;

        shellBoot = ( async () => {
            try {
                await initializeAsync();
                shellBootstrapped.value = true;
            } finally {
                bootstrappingShell.value = false;
                shellBoot = null;
            }
        } )();

        return shellBoot;
    }

    function setSelectedArticle( id: string | undefined ): void {
        if ( id ) {
            contentKey.value = articleKey;
            contentItem.value = articlesStore.articleById( id );
            markContentRead( contentItem.value as ContentfulItem | undefined, contentKey.value );
            contentModalVisible.value = true;
            reportContentViewEvent( contentItem.value, contentKey.value );
        } else {
            contentKey.value = undefined;
            contentItem.value = undefined;
            contentModalVisible.value = false;
        }
    }

    function setSelectedBonusReport( id: string | undefined ): void {
        if ( id ) {
            contentKey.value = bonusReportKey;
            contentItem.value = bonusReportsStore.bonusReportById( id );
            contentModalVisible.value = true;
            reportContentViewEvent( contentItem.value, contentKey.value );
        } else {
            contentKey.value = undefined;
            contentItem.value = undefined;
            contentModalVisible.value = false;
        }
    }

    function setSelectedTradeAlert( id: string | undefined ): void {
        if ( id ) {
            contentKey.value = tradeAlertKey;
            contentItem.value = tradeAlertsStore.tradeAlertById( id );
            markContentRead( contentItem.value as ContentfulItem | undefined, contentKey.value );
            contentModalVisible.value = true;
            reportContentViewEvent( contentItem.value, contentKey.value );
        } else {
            contentKey.value = undefined;
            contentItem.value = undefined;
            contentModalVisible.value = false;
        }
    }

    function setSelectedWebinar( id: string | undefined ): void {
        if ( id ) {
            contentKey.value = webinarKey;
            contentItem.value = webinarsStore.webinarById( id );
            markContentRead( contentItem.value as ContentfulItem | undefined, contentKey.value );
            contentModalVisible.value = true;
            reportContentViewEvent( contentItem.value, contentKey.value );
        } else {
            contentKey.value = undefined;
            contentItem.value = undefined;
            contentModalVisible.value = false;
        }
    }

    /**
     * Opens the slide-over on an item already in hand: a ?preview= link resolved by its slug (UX roadmap #14), which
     * may not be in the catalog the setSelected* lookups read (a webinar fetched on its own is never merged into it).
     */
    function setSelectedContent( key: string, item: ContentfulItem ): void {
        contentKey.value = key;
        contentItem.value = item;
        markContentRead( item, key );
        contentModalVisible.value = true;
        reportContentViewEvent( item, key );
    }

    /**
     * boot-auth-1: the slide-over was closed by a session ending, not by the member. Read once, by the `shown` watcher in
     * useContentPreviewRoute: that close must not go Back from the drawer's ?preview= entry. history.back() lands after
     * the teardown's own navigation starts (sign-in with its ?reason=, home, or the handoff's landing page), cancelling it
     * or popping past the entry it rewrote, so the member was left on the page under the drawer. Not reactive: it only
     * qualifies the close that `contentModalVisible` already reports.
     */
    let contentClosedWithoutAddress: boolean = false;

    /**
     * Closes the slide-over. `keepAddress`: the caller navigates next (or stays put on purpose), so the address is left
     * for it rather than taken back to the page underneath (boot-auth-1).
     */
    function clearSelectedContent( { keepAddress = false }: { keepAddress?: boolean } = {} ): void {
        // Only a drawer on screen has a close to qualify: a flag left behind would swallow the member's next close.
        contentClosedWithoutAddress = keepAddress && contentModalVisible.value;
        setSelectedArticle( undefined );
        setSelectedTradeAlert( undefined );
        setSelectedWebinar( undefined );
    }

    /** Whether the last close was clearSelectedContent( { keepAddress: true } ). Clears it: each close is read once. */
    function takeContentClosedWithoutAddress(): boolean {
        const kept: boolean = contentClosedWithoutAddress;
        contentClosedWithoutAddress = false;
        return kept;
    }

    /***********************************************************************************************
     AUTHENTICATION API
     ***********************************************************************************************/
    async function signUp( signUpInfo: SignUpPayload ): Promise< void > {
        try {
            // Set the loading state for the given store
            loading.value = true;

            const response = await goFetch< void, RestfulPayload >( {
                endpoint: `registrar/freemium/enroll`,
                payload: signUpInfo
            } );

            if ( response && isSuccessState( response.status ) ) {
                const credentials : LoginPayload = {
                    username: signUpInfo.email,
                    password: signUpInfo.password
                };

                await signIn( credentials );
            }
        } catch ( err: any ) {
            await resetApplication(true, true);

            throw err;
        } finally {
            // Processing is finished - successfully or unsuccessfully
            authenticating.value = false;
            loading.value = false;
        }
    }

    async function signIn( credentials: Credentials ): Promise< void > {
        try {
            // Set the loading state for the given store
            loading.value = true;

            // Attempt to load the initial state for the store
            await authenticate( credentials );
        } catch ( err: any ) {
            // UX roadmap #24: the error shows at once. The teardown no longer re-reads the public catalogs and it
            // finishes behind the message; a retry waits for it in authenticate().
            void resetApplication( true, true ).catch( ( teardownErr: unknown ) => {
                logger.error( '[ application ] the failed sign-in could not end the session', teardownErr );
            } );

            throw err;
        } finally {
            // Processing is finished - successfully or unsuccessfully
            authenticating.value = false;
            loading.value = false;
        }

        // If there are no errors (e.g.; the store has been initialized)
        // forward the user to the initial page
        if ( isAuthenticated.value ) {
            loadMemberDataBehindShell();
            await forwardUserToInitialPath();
        }
    }

    async function forwardUserToInitialPath(): Promise< void > {
        await routeManager.handleRedirectWithOptionalExplicitPath( router );
    }

    async function authenticate( credentials: Credentials ): Promise< void > {
        if ( isAuthenticated.value || authenticating.value ) {
            logger.info( `authenticating.value: ${ authenticating.value } || authenticated.value: ${ isAuthenticated.value }...returning` );
            return;
        }

        // Always clear the admin session variable (it is set when the authentication path is via the token assignment)
        adminSession.value = false;

        loading.value = true;
        authenticating.value = true;

        // A failed attempt's teardown may still be running: its purge would take the token this attempt stores.
        if ( sessionTeardown ) {
            await sessionTeardown;
        }

        logger.debug( 'authenticating' );

        await authStore.authenticate( credentials );
        logger.debug( `authenticate: success: ${ isAuthenticated.value }` );

        if ( isAuthenticated.value ) {
            await loadMemberSessionAsync();

            // The webinar catalog is permission-filtered and anonymous visitors load it too (the public-home and
            // Sector Edge carousel), but initialize() returns early on a non-empty list. Drop the public-only copy
            // now that permissions are known so the member boot (loadMemberDataBehindShell) loads the member catalog;
            // the token path gets the same from resetUserScopedState().
            webinarsStore.reset();
        }
    }

    /**
     * UX roadmap #24: what a new session needs before the member leaves the sign-in form. The grants need only the
     * userId, so once the profile is in they load beside OPRA status and the notification enrollments instead of
     * after them. Awaiting the grants here keeps the guard's self-heal from fetching them a second time on the way in.
     * Never rejects: each load catches its own failure, and the guard's self-heal covers missing grants.
     */
    async function loadMemberSessionAsync(): Promise< void > {
        await userStore.initializeProfileAsync();

        // No profile, no userId: the grants query and the account details would go out for user -1. The guard's
        // self-heal loads both once a profile is in.
        if ( ( userStore.userId() ?? -1 ) <= 0 ) {
            return;
        }

        await Promise.allSettled( [
            permissionsStore.initializeAsync(),
            userStore.loadAccountDetailsAsync()
        ] );
    }

    /**
     * UX roadmap #24: after a sign-in the feature data loads behind the member's first page, the way a hard reload of
     * a member route boots (UX roadmap #9): the shell renders at once, a private page holds its PageSkeleton until the
     * boot is done, and the Dashboard (a public route) streams its widgets. Before, the member waited on the Sign In
     * button for the whole boot.
     */
    function loadMemberDataBehindShell(): void {
        shellBootstrapped.value = false;

        // A boot still running for the previous session (signed out mid-boot) would be joined, not repeated, and it
        // skips the private phase for this member. Queue a fresh one behind it; the skeleton holds meanwhile.
        const staleBoot: Promise< void > | null = shellBoot ?? initialization;
        if ( !staleBoot ) {
            void bootstrapShell();
            return;
        }

        void staleBoot.catch( () => { /* never rejects; the next boot runs regardless */ } ).then( () => {
            shellBootstrapped.value = false;
            return bootstrapShell();
        } );
    }

    async function authenticateWithToken( authToken: string ) : Promise< void > {
        // Validate the JWT before doing anything
        const isValidJwt = authStore.isValidJwt( authToken );
        if ( !isValidJwt ) {
            logger.error( 'Invalid JWT token provided' );
            return;
        }

        // A previous session's teardown still running would purge the token assigned below.
        if ( sessionTeardown ) {
            await sessionTeardown;
        }

        // Assign the new token FIRST before clearing anything
        // This prevents route guards from seeing an unauthenticated state
        authStore.assignToken( authToken );
        // boot-auth-2: no sign-out ran, so without this the previous member's user-scoped localStorage (recent tickers,
        // video progress, favourites) and everything that drops on AUTH_RESET_EVENT (Pinia Colada, memos) carried over.
        // It leaves the token just assigned alone.
        authStore.resetUserScopedSession();

        // UX roadmap #24: only the previous member's data goes. The public catalogs are the same for everyone.
        resetUserScopedState();

        // tmi-6gh (R46): raise the flag AFTER the reset so the token-handoff flow is visible to suppression (e.g. the
        // PWA update banner) for the whole user+permissions fetch phase. The finally below clears it.
        authenticating.value = true;

        try {
            loading.value = true;
            logger.debug( `authenticate: success: ${ isAuthenticated.value }` );

            if ( isAuthenticated.value ) {
                await loadMemberSessionAsync();
            }
        } catch ( err ) {
            void resetApplication( true, true ).catch( ( teardownErr: unknown ) => {
                logger.error( '[ application ] the failed token handoff could not end the session', teardownErr );
            } );

            throw err;
        } finally {
            authenticating.value = false;
            loading.value = false;
        }

        // If there are no errors (e.g.; the store has been initialized)
        // forward the user to the initial page
        if ( isAuthenticated.value ) {
            adminSession.value = true;
            loadMemberDataBehindShell();
            await forwardUserToInitialPath();
        }
    }

    /** Everything: the public catalogs too. Only the non-logout resetApplication() still needs it. */
    function resetInternalBookkeeping(): void {
        authenticating.value = false;
        initializing.value = false;
        publicInitialized.value = false;
        compositionLoad = null;
        loading.value = false;

        authorsStore.reset();
        categoriesStore.reset();
        productsStore.reset();
        sectorsStore.reset();
        simplePagesStore.reset();
        tickersStore.reset();

        resetUserScopedState();
    }

    /**
     * UX roadmap #24: what belongs to one member: their grants, profile and entitlement-filtered content, and the sync
     * loop's session bookkeeping. The public catalogs (authors, categories, products, sectors, pages, tickers; see
     * PUBLIC_CONTENT_STORES) are the same for every visitor and stay loaded, so a sign-out or a change of user no longer
     * re-reads them. It leaves `authenticating` and `loading` alone: a sign-in in flight owns them.
     */
    function resetUserScopedState(): void {
        adminSession.value = false;
        privateInitialized.value = false;

        articlesStore.reset();
        bonusReportsStore.reset();
        coursesStore.reset();
        modelPortfoliosStore.reset();
        myPortfoliosStore.reset();
        permissionsStore.reset();
        sectorHistoryStore.reset();
        securitiesStore.reset();
        tradeAlertsStore.reset();
        webinarsStore.reset();
        userStore.reset();

        // UX roadmap #63: the content drawer is mounted by App.vue, outside the layout, so it outlived the sign-out
        // with the previous member's article or alert open. boot-auth-1: its ?preview= address is left to the caller's
        // own navigation, which replaces it; a teardown that does not navigate leaves it in place.
        clearSelectedContent( { keepAddress: true } );
        // The next session checks who the cache belongs to again: another tab may sign someone else in meanwhile.
        forgetCacheOwnerCheck();

        // A new session (sign-out, session expiry, token handoff) has not synced yet: its first
        // initializeAsync() must start a pass rather than wait out the previous session's timer.
        clearScheduledSync();
        lastSyncAt = 0;
        failedPasses = 0;
        resyncAfterPass = false;
        forceAfterPass = false;
        lastForcedPassAt = 0;
        syncGeneration++;
        for ( const key of Object.keys( syncHealth.value ) ) {
            endClientErrorStreak( syncFailureStreak( key ) );
        }
        syncHealth.value = {};
    }

    /**
     * C5 fix round 1 (Finding 2): the tickers store and/or the crosswalk store can complete
     * their FIRST successful sweep on a LATER pass than the content stores' own first hydration
     * — e.g. the crosswalk sweep fails on pass 1 while articles hydrate from cache against an
     * empty crosswalk, then a later pass fills the crosswalk but that pass's article sweep
     * itself reports zero new/changed articles, so no `articleKey` UPDATE_COMPLETE ever fires.
     * Without this, every article/trade-alert/webinar/bonus-report hydrated during the empty
     * window keeps `tickers: []` in memory for the rest of the session, even though the data to
     * resolve them now exists in IDB.
     *
     * Re-reads the ticker-dependent content stores from IDB in place — the same
     * reloadFromCacheAsync() each already runs on its own UPDATE_COMPLETE — so this only ever
     * repeats work those stores already know how to do safely, never anything new. Each store
     * re-hydrates its rows against the now-populated catalogs and swaps them in without emptying.
     */
    async function reHydrateTickerDependentContent(): Promise< void > {
        if ( reHydratingTickerDependentContent.value ) {
            return;
        }
        reHydratingTickerDependentContent.value = true;

        try {
            await Promise.allSettled( [
                articlesStore.reloadFromCacheAsync(),
                bonusReportsStore.reloadFromCacheAsync(),
                tradeAlertsStore.reloadFromCacheAsync(),
                webinarsStore.reloadFromCacheAsync()
            ] );
        } finally {
            reHydratingTickerDependentContent.value = false;
        }
    }

    /**
     * Brings the store for `key` up to date with what the sync worker just wrote to the cache (UPDATE_COMPLETE).
     *
     * Content stores and the sector history re-read in place (reloadFromCacheAsync): the catalog on screen and its
     * loading flag stay as they are until the new catalog is complete (tmi-e7w.22). The catalog stores still reset
     * and re-initialize. Resolves once the store reflects the cache; a store never rejects from here.
     */
    async function applyCacheUpdate( key: string ): Promise< void > {
        switch ( key ) {
            case articleKey:
                await articlesStore.reloadFromCacheAsync();
                break;
            case bonusReportKey:
                await bonusReportsStore.reloadFromCacheAsync();
                break;
            case tradeAlertKey: {
                // gap_alert_delivery_and_notifications-2: what the store held before the re-read, so NewAlertAnnouncer
                // hears only the alerts this update brought (new-trade-alerts.ts decides what counts as new).
                const before = tradeAlertIdSnapshot( tradeAlertsStore.allTradeAlerts );
                await tradeAlertsStore.reloadFromCacheAsync();
                recordTradeAlertArrivals( before, tradeAlertsStore.allTradeAlerts );
                break;
            }
            case webinarKey:
                await webinarsStore.reloadFromCacheAsync();
                break;
            case authorsSyncKey:
                authorsStore.reset();
                await authorsStore.initialize();
                break;
            case categoriesSyncKey:
                categoriesStore.reset();
                await categoriesStore.initialize();
                break;
            case productsSyncKey:
                productsStore.reset();
                await productsStore.initializeAsync();
                break;
            case sectorsSyncKey:
                sectorsStore.reset();
                await sectorsStore.initializeAsync();
                break;
            case tickersSyncKey: {
                // C5 fix round 1 (Finding 2): capture BEFORE reset — an empty
                // catalog transitioning to populated is the signal that content
                // hydrated earlier against an empty catalog needs a re-pass.
                const wasEmpty = 0 === tickersStore.allTickers.length;
                tickersStore.reset();
                await tickersStore.initialize();
                if ( wasEmpty && tickersStore.allTickers.length > 0 ) {
                    void reHydrateTickerDependentContent();
                }
                break;
            }
            case simplePagesSyncKey:
                simplePagesStore.reset();
                await simplePagesStore.initializeAsync();
                break;
            case sectorAlertsKey:
                // DP-22: re-read without emptying the store or moving the selected date.
                await sectorHistoryStore.reloadFromCacheAsync();
                break;
        }
    }

    function syncFailureStreak( key: string ): string {
        return `sync:${ key }`;
    }

    /**
     * UX roadmap #58: records a key's SYNC_STATUS. A failure is reported once per streak, and not while offline, which
     * explains it; `report` is false for another tab's status, which that tab reports.
     */
    function recordSyncStatus( key: string, status: SyncStatusReport, report: boolean ): void {
        const next = nextSyncKeyHealth( syncHealth.value[ key ], status, Date.now() );
        syncHealth.value = { ...syncHealth.value, [ key ]: next };

        if ( next.ok ) {
            endClientErrorStreak( syncFailureStreak( key ) );
        } else if ( report && navigator.onLine ) {
            queueClientErrorOncePerStreak(
                syncFailureStreak( key ),
                new Error( `The background sync of ${ key } failed` ),
                { sync: key, lastOkAt: next.lastOkAt }
            );
        }
    }

    function currentCacheOwner(): string | null {
        return deriveCacheOwnerId( authToken.value );
    }

    /** UX roadmap #18: opened with the worker and closed in terminateWorker (a sign-out, a pagehide). */
    function ensureSyncChannel(): void {
        if ( syncChannel || 'undefined' === typeof BroadcastChannel ) {
            return;
        }
        syncChannel = new BroadcastChannel( SYNC_BROADCAST_CHANNEL );
        syncChannel.onmessage = ( msg: MessageEvent< SyncBroadcast > ) => {
            void applySyncBroadcast( msg.data );
        };
    }

    /**
     * Relays this tab's worker news to the member's other tabs. Relayed from here rather than posted by the worker: a
     * BroadcastChannel never delivers to the object that posted, so this tab does not get its own news twice.
     */
    function broadcastSyncEvent( event: DataSyncEvent ): void {
        const message: SyncBroadcast = {
            eventType: event.eventType,
            payload: event.payload,
            ok: event.ok,
            at: event.at,
            owner: currentCacheOwner()
        };
        try {
            syncChannel?.postMessage( message );
        } catch ( err: unknown ) {
            logger.warn( '[ application ] could not relay a sync update to the other tabs', err );
        }
    }

    /**
     * Another tab's worker wrote the shared cache. Cooldowns are shared, so this tab's own worker would find those
     * rows current and report nothing: without this, its bell, counts and lists stayed stale until a reload.
     * Only for the same member's rows, and never while this tab is signing out or about to reload.
     */
    async function applySyncBroadcast( message: SyncBroadcast | undefined ): Promise< void > {
        if ( !message?.payload || reloadPending || !isAuthenticated.value || sessionTeardown ) {
            return;
        }
        if ( message.owner !== currentCacheOwner() ) {
            return;
        }

        if ( MessageTypes.UPDATE_COMPLETE === message.eventType ) {
            await applyCacheUpdate( message.payload );
        } else if ( MessageTypes.SYNC_STATUS === message.eventType ) {
            recordSyncStatus( message.payload, message, false );
        }
    }

    function ensureWorker(): Worker {
        if ( !worker ) {
            worker = new Worker( new URL( '@/services/data/sync-worker.ts?worker', import.meta.url ), { type: 'module' } );

            worker.onerror = function ( event: ErrorEvent ): void {
                logger.error( `Sync worker error: ${ event.message }` );
                // A worker that failed to load never answers another REQUEST_SECURE_SYNC, so the next pass
                // gets a fresh one rather than posting into the void and sticking `syncInProgress` (lifecycle-1).
                terminateWorker();
                scheduleNextSync( MaintenanceRefreshInterval );
            };

            worker.onmessage = async function ( msg: MessageEvent< DataSyncEvent > ): Promise< void > {
                const messageType: MessageTypes = msg.data.eventType;
                const payload = msg.data.payload;
                if ( MessageTypes.UPDATE_COMPLETE === messageType ) {
                    if ( payload ) {
                        // Relayed only for this session's pass: the tag names the owner signed in now.
                        if ( passGeneration === syncGeneration ) {
                            broadcastSyncEvent( msg.data );
                        }
                        await applyCacheUpdate( payload );
                    }
                } else if ( MessageTypes.SYNC_STATUS === messageType ) {
                    // A pass for the session before a token handoff says nothing about this one's content.
                    if ( payload && passGeneration === syncGeneration ) {
                        broadcastSyncEvent( msg.data );
                        recordSyncStatus( payload, msg.data, true );
                    }
                } else if ( MessageTypes.LOAD_COMPLETE === messageType ) {
                    syncInProgress.value = false;
                    passForced = false;
                    const resync = resyncAfterPass;
                    resyncAfterPass = false;
                    const forceNext = forceAfterPass;
                    forceAfterPass = false;
                    if ( passGeneration !== syncGeneration || resync || forceNext ) {
                        // The pass ran for the session before a token handoff: this one has not synced yet. Or it
                        // started offline and the device has come back since (UX roadmap #58). Or the member asked
                        // for a refresh while it ran, and an ordinary pass skips what is inside its cooldown (#107).
                        fireDataSyncEvent( forceNext );
                    } else if ( false === msg.data.ok ) {
                        // gap_alert_delivery-1: a failed pass retries at the coordinators' 15 s cooldown, backing
                        // off while the failures continue, instead of leaving the member on stale data for 5 minutes.
                        failedPasses++;
                        scheduleNextSync( Math.min( SyncRetryInterval * 2 ** ( failedPasses - 1 ), MaintenanceRefreshInterval ) );
                    } else {
                        failedPasses = 0;
                        lastSyncAt = Date.now();
                        scheduleNextSync( MaintenanceRefreshInterval );
                    }
                } else if ( MessageTypes.RELOAD_REQUIRED === messageType ) {
                    // The worker's cache connection was closed by a newer build's upgrade. Its module state is
                    // gone with it; a reload brings both threads back on the new bundle. `reloadPending` stops
                    // fireDataSyncEvent() from spawning a replacement worker on the old bundle in the meantime,
                    // and keeps the reload itself to exactly one (R44) — which may wait for a sign-in (tmi-cya #6).
                    terminateWorker();
                    if ( reloadPending ) {
                        return;
                    }
                    logger.warn( '[ application ] the sync worker reported a cache schema change — reloading' );
                    reloadOnceNotAuthenticating();
                } else {
                    await handleErrorsAsync( messageType, payload );
                }
            };
        }
        ensureSyncChannel();

        return worker;
    }

    function clearScheduledSync(): void {
        if ( null !== nextSyncTimer ) {
            clearTimeout( nextSyncTimer );
            nextSyncTimer = null;
        }
    }

    /**
     * The only place the loop arms its timer, so there is never more than one (lifecycle-11). Hidden, it is never sooner
     * than MaintenanceRefreshInterval (data-sync-1); resumeSync() catches up when the member returns.
     */
    function scheduleNextSync( delay: number ): void {
        clearScheduledSync();
        if ( reloadPending ) return;

        nextSyncTimer = setTimeout( () => {
            nextSyncTimer = null;
            fireDataSyncEvent( false, true );
        }, 'hidden' === visibility.value ? Math.max( delay, MaintenanceRefreshInterval ) : delay );
    }

    /**
     * @param force UX roadmap #107: the worker skips the pass and per-coordinator cooldowns (a member's refresh).
     * @param fromTimer the loop's own timer (data-sync-1): the one caller that syncs a hidden tab.
     */
    function fireDataSyncEvent( force: boolean = false, fromTimer: boolean = false ) {
        // A sign-out in flight keeps the token until the HTTP cache is gone: a pass started now would spawn a worker
        // with the old token that outlives the session.
        if ( reloadPending || !isAuthenticated.value || syncInProgress.value || sessionTeardown ) return;
        if ( 'hidden' === visibility.value && !fromTimer ) {
            // Hidden, only the timer syncs. A pass asked for now (a boot in a background tab, a session's first pass
            // after a token handoff) still happens, at the background cadence.
            if ( null === nextSyncTimer ) {
                scheduleNextSync( MaintenanceRefreshInterval );
            }
            return;
        }
        // A pass fired early (a catch-up) replaces the pending timer; its LOAD_COMPLETE arms the next one.
        clearScheduledSync();
        syncInProgress.value = true;
        passGeneration = syncGeneration;
        passStartedOffline = !navigator.onLine;
        passForced = force;
        if ( force ) {
            lastForcedPassAt = Date.now();
        }

        try {
            const syncEvent = createDataSyncEvent( MessageTypes.REQUEST_SECURE_SYNC, permissionsStore.authorizationGrants() );
            if ( force ) {
                syncEvent.force = true;
            }
            ensureWorker().postMessage( syncEvent );
        } catch ( err: any ) {
            logger.error( `Failed to dispatch sync event: ${ err?.message }` );
            // No pass started, so no LOAD_COMPLETE will clear the flag (lifecycle-1).
            syncInProgress.value = false;
            passForced = false;
            scheduleNextSync( MaintenanceRefreshInterval );
        }
    }

    /**
     * lifecycle-11: ~40 call sites await initializeAsync() on navigation. Once the loop runs, its own timer owns
     * the cadence; only a session that has not synced yet (first load, after sign-in) starts a pass from here.
     */
    function requestSyncAfterInitialization(): void {
        if ( null !== nextSyncTimer || ( lastSyncAt > 0 && Date.now() - lastSyncAt < ResumeCatchUpAfterMilliseconds ) ) return;
        fireDataSyncEvent();
    }

    /**
     * The member is back (tab visible again, back online, page restored from the back/forward cache): sync now when
     * the last clean pass is over a minute old or a pass failed since, otherwise keep the loop's timer (re-armed if a
     * pagehide stopped it).
     */
    function resumeSync(): void {
        if ( reloadPending || !isAuthenticated.value || syncInProgress.value || sessionTeardown ) return;
        // A page restored from the back/forward cache closed the channel on pagehide; its timer may not fire for minutes.
        ensureSyncChannel();

        if ( failedPasses > 0 || Date.now() - lastSyncAt >= ResumeCatchUpAfterMilliseconds ) {
            fireDataSyncEvent();
        } else if ( null === nextSyncTimer ) {
            scheduleNextSync( Math.max( 0, lastSyncAt + MaintenanceRefreshInterval - Date.now() ) );
        }
    }

    /**
     * UX roadmap #58: sync now, however recent the last pass: the device is back online, or the member asked. Back
     * online after a while, the loop sat on a backoff of up to five minutes, and resumeSync() skipped a pass still in
     * flight. The backoff built up offline is dropped, so a failure now retries at 15 s. A pass in flight that started
     * offline is followed at once by a new one; one that started online is already the fresh pass.
     */
    function resyncNow(): void {
        if ( reloadPending || !isAuthenticated.value || sessionTeardown || 'hidden' === visibility.value ) return;

        failedPasses = 0;
        if ( syncInProgress.value ) {
            resyncAfterPass = resyncAfterPass || passStartedOffline;
            return;
        }
        fireDataSyncEvent();
    }

    /**
     * UX roadmap #107: a content list's refresh. resyncNow() alone fetched nothing between two loop passes: the worker's
     * 30 s pass cooldown and each coordinator's 5 minute one were always unexpired, so only the trade alert head check
     * ran and the button stopped spinning over an unchanged list. This pass skips both. A tap while an ordinary pass
     * runs forces the next one as soon as it ends; within ForcedSyncFloor of the last forced pass it is a resyncNow().
     */
    function refreshContentNow(): void {
        if ( reloadPending || !isAuthenticated.value || sessionTeardown || 'hidden' === visibility.value ) return;

        if ( Date.now() - lastForcedPassAt < ForcedSyncFloor ) {
            resyncNow();
            return;
        }

        failedPasses = 0;
        if ( syncInProgress.value ) {
            forceAfterPass = forceAfterPass || !passForced;
            return;
        }
        fireDataSyncEvent( true );
    }

    /*
     * UX roadmap #16: the three ways a session ends under the member, besides the worker's UNAUTHORIZED. Before, a 401
     * on the main thread was posted to a window listener that dropped it, and the token's expiry went unnoticed until
     * something wrote localStorage, so the app kept its old data while every request failed for minutes. The window
     * 'message' listener is gone: nothing posted what it accepted, and it took messages from any frame.
     */
    // A 401 that goFetch or a Pinia Colada query confirmed with the server.
    onScopeDispose( clientStateManager.onUnauthorized( endRejectedSession ) );
    // The Auth store's token ran out (its expiry timer, or the re-check when the tab is shown again).
    const onSessionExpired = (): void => {
        void endSessionOnExpiry();
    };
    window.addEventListener( SESSION_EXPIRED_EVENT, onSessionExpired );
    onScopeDispose( () => window.removeEventListener( SESSION_EXPIRED_EVENT, onSessionExpired ) );
    // UX roadmap #63: another tab signed out, or saw the token run out first. This tab kept the private pages up.
    const onSessionEndedElsewhere = ( event: Event ): void => {
        const detail: SessionEndedElsewhereDetail | undefined = ( event as CustomEvent< SessionEndedElsewhereDetail > ).detail;
        // data-sync-3: shared storage as it is now (the other tab has emptied it). Should another member sign in before
        // the teardown reaches the token, their token and user-scoped keys are left alone.
        void endSessionOnExpiry( detail?.expired ? 'expired' : 'signed-out-elsewhere', { onlyIfStoredTokenIs: authStore.readStoredToken() } );
    };
    window.addEventListener( SESSION_ENDED_ELSEWHERE_EVENT, onSessionEndedElsewhere );
    onScopeDispose( () => window.removeEventListener( SESSION_ENDED_ELSEWHERE_EVENT, onSessionEndedElsewhere ) );

    async function handleErrorsAsync( messageType: MessageTypes, payload?: string | undefined ): Promise< void > {
        if ( MessageTypes.UNAUTHORIZED === messageType ) {
            await endSessionOnExpiry();
        } else if ( MessageTypes.FORBIDDEN === messageType ) {
            logger.error( `Received forbidden error: ${ JSON.stringify( payload ) }` );
        } else if ( MessageTypes.INTERNAL_ERROR === messageType ) {
            logger.error( `Received internal error: ${ JSON.stringify( payload ) }` );
        } else if ( MessageTypes.NOT_FOUND === messageType ) {
            logger.error( `Received not found error: ${ JSON.stringify( payload ) }` );
        } else if ( MessageTypes.CONFIGURATION === messageType ) {
            logger.error( `Received a configuration error: ${ JSON.stringify( payload ) }` );
        } else if ( MessageTypes.UNKNOWN === messageType ) {
            logger.error( `Received an unknown error: ${ JSON.stringify( payload ) }` );
        }
    }

    function isAdminSession(): boolean {
        return adminSession.value;
    }

    /**
     * THE way a session ends because the token is gone or refused (as opposed to the member signing out): stop the
     * sync, drop the member's data and token, and send the member to sign-in with `?reason=expired`, returning to the
     * page they were on once they sign in again. Concurrent calls share one run, and a sign-out in flight wins.
     *
     * UX roadmap #16: every page goes to sign-in, public ones included. A member on the Dashboard or a public article
     * used to stay put while the page silently turned into its signed-out version, with nothing saying why.
     *
     * `reason` picks the notice above the form: 'expired' by default, 'signed-out-elsewhere' when another tab signed out
     * (UX roadmap #63). `teardown` is passed to Auth's reset (data-sync-3).
     */
    async function handleUnauthorizedSessionExpiry( reason: SignInReason = 'expired', teardown?: AuthResetOptions ): Promise< void > {
        if ( handlingUnauthorized.value || sessionTeardown ) {
            return;
        }

        handlingUnauthorized.value = true;

        try {
            const currentRoute: RouteLocationNormalizedLoaded = router.currentRoute.value;

            // resetApplication( true, true ), with Auth's reset options.
            await endSessionAndLandAsync( null, teardown );

            if ( !routeManager.isSignInPath( currentRoute.path ) ) {
                await routeManager.handleForwardToSignInAndReturnAsync( currentRoute.fullPath || currentRoute.path, router, reason );
            }
        } finally {
            handlingUnauthorized.value = false;
        }
    }

    /** Never rejects: it runs from event listeners. A failure leaves the member signed out wherever they are. */
    async function endSessionOnExpiry( reason?: SignInReason, teardown?: AuthResetOptions ): Promise< void > {
        try {
            await handleUnauthorizedSessionExpiry( reason, teardown );
        } catch ( err: unknown ) {
            logger.error( '[ application ] the expired session could not be ended', err );
            clientStateManager.queueClientError( err, { request: 'session expiry' } );
        }
    }

    /**
     * A request was refused for an expired session. Only while this tab still counts as signed in: a request sent
     * with the previous token and answered after the session ended has nothing left to end.
     */
    function endRejectedSession(): void {
        if ( isAuthenticated.value ) {
            void endSessionOnExpiry();
        }
    }

    /**
     * Ends the session, in this order: stop the sync worker (terminateWorker also clears the loop's flag and timer),
     * drop the service worker's API response cache so nothing the member fetched is served afterwards, purge the token
     * and user-scoped storage (Auth.reset, with `teardown`), and empty the member's stores. Never rejects on the HTTP
     * cache. A caller that joins a teardown in flight gets that one's options.
     */
    function endSessionAsync( teardown?: AuthResetOptions ): Promise< void > {
        if ( !sessionTeardown ) {
            sessionTeardown = ( async () => {
                terminateWorker();
                // Kept even once no runtime rule fills this cache: clients that ran an older build still hold it.
                if ( 'caches' in window ) {
                    try {
                        await caches.delete( 'tmi-api-cache' );
                    } catch ( err: unknown ) {
                        // A cache that would not delete must not leave the member signed in. The logger is a no-op in
                        // production, and the member's cached responses outliving the session needs to be seen.
                        logger.warn( '[ application ] could not delete the API response cache on sign-out', err );
                        clientStateManager.queueClientError( err, { request: 'sign-out: delete tmi-api-cache' } );
                    }
                }
                await authStore.logout( teardown );
                resetUserScopedState();
            } )().finally( () => {
                sessionTeardown = null;
            } );
        }

        return sessionTeardown;
    }

    async function resetApplication( logout?: boolean, doNotNavigate?: boolean ) {
        if ( !logout ) {
            resetInternalBookkeeping();
            await initializeAsync();
            shellBootstrapped.value = true;
            return;
        }

        // UX roadmap #24: the member goes home as soon as the session is gone. This used to re-read every public
        // catalog first (and a failed sign-in waited for the same before showing its error); those stay loaded now.
        await endSessionAndLandAsync( doNotNavigate ? null : routeManager.homePath() );
    }

    /** Ends the session, then navigates to `path` unless it is null. A navigating sign-out holds isSigningOut until it lands. */
    async function endSessionAndLandAsync( path: string | null, teardown?: AuthResetOptions ): Promise< void > {
        if ( null !== path ) {
            signingOut.value++;
        }

        try {
            await endSessionAsync( teardown );
            shellBootstrapped.value = true;

            if ( null !== path ) {
                await routeManager.handleRedirectWithOptionalExplicitPath( router, path );
            }
        } finally {
            if ( null !== path ) {
                signingOut.value--;
            }
        }
    }

    async function logoutAndGoHome(): Promise< void > {
        await resetApplication( true );
    }

    /**
     * Ends the session and lands on sign-in, with the notice for `reason` above the form. UX roadmap #87: a password
     * change from Profile used to drop the member on the public home page with no way back to their account.
     */
    async function logoutAndGoToSignIn( reason?: SignInReason ): Promise< void > {
        const signInPath: string = routeManager.signInPath();
        await endSessionAndLandAsync( reason ? `${ signInPath }?${ SignInReasonQueryKey }=${ reason }` : signInPath );
    }

    async function nukeItFromOrbitItsTheOnlyWayToBeSure(): Promise< void > {
        await resetApplication( true );
        localStorage.clear();

        try {
            await resetCachedContent();
        } catch ( err: unknown ) {
            // This is the user's last-resort recovery tool: a cache that would not empty must not strand
            // the rest of the teardown (HTTP caches, service workers, the redirect) — none of which touch
            // IndexedDB, so it runs even on a quiet stop (F-M1).
            // A CacheClosedError means a newer build already owns the database and a reload is in flight —
            // nothing left to clear and nothing to report.
            if ( !( err instanceof CacheClosedError ) ) {
                // The Error itself, not its message: the stack and the CacheError `cause` are the diagnosis.
                // The runtime logger spreads an ARRAY of extra arguments into console.error.
                logger.error( '[ application ] the full reset could not empty the cache', err );
            }
        }

        if ( 'caches' in window ) {
            const names = await caches.keys();
            await Promise.all( names.map( name => caches.delete( name ) ) );
        }
        if ( navigator.serviceWorker ) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all( registrations.map( r => r.unregister() ) );
        }

        // Forward the new session to the default landing page
        const currentPath: RouteLocationNormalizedLoaded | undefined = router?.currentRoute.value;
        if ( currentPath?.fullPath !== routeManager.homePath() ) {
            await router.push( routeManager.homePath() );
        } else {
            // refresh the current page
            router.go( 0 );
        }
    }

    function terminateWorker() {
        if ( worker ) {
            worker.terminate();
            worker = null;
        }
        // lifecycle-1: the LOAD_COMPLETE that would clear the flag can no longer arrive. Left set, it stopped every
        // later pass (a session expiry mid-pass, a sign-out, a pagehide) until a hard reload.
        syncInProgress.value = false;
        passForced = false;
        resyncAfterPass = false;
        forceAfterPass = false;
        clearScheduledSync();
        // UX roadmap #18: a signed-out (or hidden-for-good) tab takes no more news from the others.
        if ( syncChannel ) {
            syncChannel.close();
            syncChannel = null;
        }
    }

    /**
     * UX roadmap #16: goFetch throws Unauthorized once the server has confirmed the session is gone, and a caller that
     * does not catch it lands here. That is a session expiry, handled: end the session and keep it off the console.
     * Anything else stays an unhandled rejection for the browser to report.
     */
    function handleRejectedPromise( event: PromiseRejectionEvent ): void {
        const rejectionReason = event.reason;
        if ( rejectionReason instanceof Unauthorized ) {
            event.preventDefault();
            endRejectedSession();
            return;
        }

        logger.warn( `Unhandled Promise Rejection: REASON: ${ JSON.stringify( rejectionReason ) }` );
    }

    return {
        // STATE
        compositionCtx,
        contentModalVisible,
        contentItem,
        contentKey,
        dateOfMostRecentMarketDataAvailable,

        // GETTERS
        isAuthenticating,
        isLoading,
        isNavigationReady,
        isShellReady,
        isSigningOut,
        isSyncing,
        lastContentSyncAt,
        syncHealth: currentSyncHealth,

        // ACTIONS
        applyCacheUpdate,
        authenticateWithToken,
        bootstrapShell,
        clearSelectedContent,
        compositionReadyAsync,
        handleRejectedPromise,
        handleUnauthorizedSessionExpiry,
        initializeAsync,
        isAdminSession,
        logoutAndGoHome,
        logoutAndGoToSignIn,
        nukeItFromOrbitItsTheOnlyWayToBeSure,
        refreshContentNow,
        refreshMarketDateUnlessCached,
        resetApplication,
        resumeSync,
        resyncNow,
        setSelectedArticle,
        setSelectedBonusReport,
        setSelectedContent,
        setSelectedTradeAlert,
        setSelectedWebinar,
        signIn,
        signUp,
        takeContentClosedWithoutAddress,
        terminateWorker
    };
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useApplicationStore, import.meta.hot ) );
}
